/**
 * Auto-assign Medical Assistant Utility Functions
 *
 * This utility provides reusable functions for automatically assigning Medical Assistants
 * based on patient language preferences and MA language weights.
 * Used by both the useAutoAssignMA hook and appointment creation flows.
 */
import {
  AppointmentType,
  Language,
  MedicalAssistant,
  SpecialtyType,
} from "~/api/API";
import {
  AutoAssignMAResult,
  currentMASupportsLanguage,
  getEligibleMAs,
  getFallbackMA,
  getMADisplayName,
  getMAsWithSpecialtyWeights,
  getMAsWithWeights,
  getSpecialtyEligibleMAs,
  isEligibleAppointmentType,
  selectMAByWeight,
  validateAssignmentPrerequisites,
} from "./autoAssignMACommon";
import { DEFAULT_DAILY_MA_LIMIT } from "./autoAssignMAConstants";
import { selectMAFairly, WeeklyAppointmentData } from "./autoAssignMAFair";

export type { AutoAssignMAResult };
// Re-export common functions for backward compatibility
export {
  currentMASupportsLanguage,
  getEligibleMAs,
  getFallbackMA,
  getMADisplayName,
  getMAsWithSpecialtyWeights,
  getMAsWithWeights,
  getSpecialtyEligibleMAs,
  isEligibleAppointmentType,
  selectMAByWeight,
  validateAssignmentPrerequisites,
};

export interface AutoAssignMAOptions {
  patientLanguage: Language | null;
  appointmentType: AppointmentType | null;
  currentMAId?: string | null;
  allAssistants: MedicalAssistant[] | null;
  appointmentDate?: Date | null; // New: for fair assignment
  weeklyData?: WeeklyAppointmentData | null; // New: pre-fetched weekly data
}

/**
 * Core auto-assign MA logic that determines if assignment should happen and selects MA
 *
 * @param options - Configuration for auto-assignment
 * @param forceAssignOnTypeSet - If true, assigns MA even if current MA supports language (for initial type selection)
 * @returns Assignment result with shouldAssign flag, selected MA ID, and reason
 */
export const performAutoAssignMA = (
  options: AutoAssignMAOptions,
  forceAssignOnTypeSet = false,
): AutoAssignMAResult => {
  const {
    patientLanguage,
    appointmentType,
    allAssistants,
    appointmentDate,
    weeklyData,
  } = options;

  // Treat null patient language as English for assignment purposes
  const effectiveLanguage = patientLanguage || Language.ENGLISH;

  // Check eligibility
  if (!isEligibleAppointmentType(appointmentType)) {
    return {
      shouldAssign: false,
      selectedMAId: null,
      reason: "Appointment type not eligible for auto-assignment",
    };
  }

  // Get eligible MAs
  const eligibleMAs = getEligibleMAs(allAssistants, effectiveLanguage);
  if (!eligibleMAs.length) {
    return {
      shouldAssign: false,
      selectedMAId: null,
      reason: "No eligible MAs found for language",
    };
  }

  // Use fair assignment algorithm if we have appointment date and weekly data
  if (appointmentDate && weeklyData) {
    // Always assign when called (hook controls when to call this)
    const selectedMAId = selectMAFairly({
      eligibleMAs,
      appointmentDate,
      weeklyData,
      language: effectiveLanguage,
      dailyLimit: DEFAULT_DAILY_MA_LIMIT,
    });

    if (!selectedMAId) {
      return {
        shouldAssign: false,
        selectedMAId: null,
        reason: "Failed to select MA using fair assignment",
      };
    }

    const languageDisplay =
      effectiveLanguage.charAt(0).toUpperCase() +
      effectiveLanguage.slice(1).toLowerCase();

    return {
      shouldAssign: true,
      selectedMAId,
      reason: `Fair-assigned based on ${languageDisplay} language preference and weekly distribution`,
    };
  }

  // Fall back to legacy weighted random selection
  // Always assign when called (hook controls when to call this)
  const masWithWeights = getMAsWithWeights(eligibleMAs, effectiveLanguage);
  if (!masWithWeights.length) {
    return {
      shouldAssign: false,
      selectedMAId: null,
      reason: "No MAs with valid weights found",
    };
  }

  const selectedMAId = selectMAByWeight(masWithWeights);

  if (!selectedMAId) {
    return {
      shouldAssign: false,
      selectedMAId: null,
      reason: "Failed to select MA by weight",
    };
  }

  const languageDisplay =
    effectiveLanguage.charAt(0).toUpperCase() +
    effectiveLanguage.slice(1).toLowerCase();

  return {
    shouldAssign: true,
    selectedMAId,
    reason: `Auto-assigned based on ${languageDisplay} language preference`,
  };
};

/**
 * Legacy auto-assign MA logic with specialty-based assignment and Helen Jiang fallback
 * Used by legacy hook when fair assignment is not available
 */
export const performLegacyAutoAssignMA = (
  options: {
    patientLanguage: Language | null;
    appointmentType: AppointmentType | null;
    currentMAId?: string | null;
    allAssistants: MedicalAssistant[] | null;
    doctorSpecialties?: SpecialtyType[] | null;
  },
  forceAssignOnTypeSet = false,
): AutoAssignMAResult => {
  const {
    patientLanguage,
    appointmentType,
    currentMAId,
    allAssistants,
    doctorSpecialties,
  } = options;

  // Treat null patient language as English for assignment purposes
  const effectiveLanguage = patientLanguage || Language.ENGLISH;

  // 1. Check eligibility
  if (!isEligibleAppointmentType(appointmentType)) {
    return {
      shouldAssign: false,
      selectedMAId: null,
      reason: "Appointment type not eligible for auto-assignment",
    };
  }

  // 2. Validate prerequisites
  const validation = validateAssignmentPrerequisites(
    appointmentType,
    effectiveLanguage,
    allAssistants,
  );
  if (!validation.isValid) {
    return {
      shouldAssign: false,
      selectedMAId: null,
      reason: validation.reason,
    };
  }

  // 3. Check if current MA supports language AND specialty (if not forcing)
  if (!forceAssignOnTypeSet) {
    const currentMASupportsLang = currentMASupportsLanguage(
      currentMAId || null,
      allAssistants,
      effectiveLanguage,
    );

    // If we have doctor specialties, always reassign to find the best specialty-specific match
    if (doctorSpecialties && doctorSpecialties.length > 0) {
      // Always reassign when specialties are involved to ensure optimal specialty matching
    } else if (currentMASupportsLang) {
      // No specialties required, just check language support
      return {
        shouldAssign: false,
        selectedMAId: null,
        reason: "Current MA supports language",
      };
    }
  }

  // 4. Try specialty-aware assignment (includes both specialty-specific and global fallback)
  if (doctorSpecialties && doctorSpecialties.length > 0) {
    const specialtyEligibleMAs = getSpecialtyEligibleMAs(
      allAssistants,
      effectiveLanguage,
      doctorSpecialties,
    );

    if (specialtyEligibleMAs.length > 0) {
      const masWithSpecialtyWeights = getMAsWithSpecialtyWeights(
        specialtyEligibleMAs,
        effectiveLanguage,
        doctorSpecialties,
      );

      if (masWithSpecialtyWeights.length > 0) {
        const selectedMAId = selectMAByWeight(masWithSpecialtyWeights);
        if (selectedMAId) {
          // Determine if this was specialty-specific or global weight assignment
          const selectedMA = allAssistants?.find(
            (ma) => ma.id === selectedMAId,
          );
          let usedSpecialtySpecific = false;

          if (selectedMA?.assignmentCriteria?.languageWeights) {
            usedSpecialtySpecific =
              selectedMA.assignmentCriteria.languageWeights.some(
                (lw) =>
                  lw.language === effectiveLanguage &&
                  lw.doctorSpecialty &&
                  doctorSpecialties.includes(lw.doctorSpecialty) &&
                  lw.weight > 0,
              );
          }

          return {
            shouldAssign: true,
            selectedMAId,
            reason: usedSpecialtySpecific
              ? "Specialty-specific assignment"
              : "Global weight assignment (specialty fallback)",
          };
        }
      }
    }
  }

  // 5. Fallback to legacy weighted assignment (for MAs without assignmentCriteria)
  const eligibleMAs = getEligibleMAs(allAssistants, effectiveLanguage);
  const masWithWeights = getMAsWithWeights(eligibleMAs, effectiveLanguage);

  if (masWithWeights.length > 0) {
    const selectedMAId = selectMAByWeight(masWithWeights);
    if (selectedMAId) {
      return {
        shouldAssign: true,
        selectedMAId,
        reason: "Legacy assignment (no assignment criteria configured)",
      };
    }
  }

  // 6. Final fallback to environment-specific MA
  const fallbackMA = getFallbackMA(allAssistants, effectiveLanguage);
  if (fallbackMA) {
    return {
      shouldAssign: true,
      selectedMAId: fallbackMA.id,
      reason: "Environment fallback assignment",
    };
  }

  // 7. No assignment possible
  const languageDisplay =
    effectiveLanguage.charAt(0).toUpperCase() +
    effectiveLanguage.slice(1).toLowerCase();
  return {
    shouldAssign: false,
    selectedMAId: null,
    reason: `No eligible MAs found for ${languageDisplay} language and no fallback MA available`,
  };
};
